/**
 * Simple Workflow Storage Utilities
 * Direct localStorage interface for workflows
 */

export interface SavedWorkflow {
  id: string;
  name: string;
  description?: string;
  workflow: any;
  metadata: {
    nodeCount: number;
    connectionCount: number;
    createdAt: string;
    updatedAt: string;
  };
  isDirty: boolean;
  
  // NEW: Simple function flag
  isFunction?: boolean; // If true, this workflow appears as a node in other workflows
}

export class WorkflowStorageUtils {
  private static readonly WORKFLOWS_KEY = 'sengilabs-saved-workflows';
  private static readonly WORKFLOW_PREFIX = 'sengilabs-canvas-';

  /**
   * Get all saved workflows
   */
  static getAllSavedWorkflows(): SavedWorkflow[] {
    try {
      const saved = localStorage.getItem(this.WORKFLOWS_KEY);
      if (!saved) return [];
      
      const workflowIds: string[] = JSON.parse(saved);
      const workflows: SavedWorkflow[] = [];
      
      for (const id of workflowIds) {
        const workflowData = localStorage.getItem(`${this.WORKFLOW_PREFIX}${id}`);
        if (workflowData) {
          try {
            const parsed = JSON.parse(workflowData);
            workflows.push({
              id,
              name: parsed.name || `Workflow ${id}`,
              description: parsed.description,
              workflow: parsed,
              metadata: {
                nodeCount: Object.keys(parsed.nodes || {}).length,
                connectionCount: (parsed.connections || []).length,
                createdAt: parsed.metadata?.createdAt || new Date().toISOString(),
                updatedAt: parsed.metadata?.updatedAt || new Date().toISOString()
              },
              isDirty: false,
              isFunction: parsed.isFunction || false // NEW: Load isFunction flag from stored data
            });
          } catch (parseError) {
            console.warn('Failed to parse workflow:', id, parseError);
          }
        }
      }
      
      return workflows.sort((a, b) => 
        new Date(b.metadata.updatedAt).getTime() - new Date(a.metadata.updatedAt).getTime()
      );
    } catch (error) {
      console.error('Failed to load saved workflows:', error);
      return [];
    }
  }

  /**
   * Save a workflow
   */
  static saveWorkflow(id: string, workflowData: any, name: string, description?: string, isFunction?: boolean): boolean {
    try {
      const now = new Date().toISOString();
      
      // Preserve existing isFunction flag if not provided
      let currentIsFunction = isFunction;
      if (currentIsFunction === undefined) {
        const existingWorkflow = this.getAllSavedWorkflows().find(w => w.id === id);
        currentIsFunction = existingWorkflow?.isFunction || false;
      }
      
      const savedWorkflow = {
        ...workflowData,
        id,
        name,
        description,
        isFunction: currentIsFunction, // NEW: Store isFunction flag directly in workflow data
        meta: {
          ...workflowData.meta,
          name: name,
          version: workflowData.meta?.version || '1.0.0',
          created: workflowData.meta?.created || now,
          updated: now,
          description: description || workflowData.meta?.description
        },
        metadata: {
          ...workflowData.metadata,
          updatedAt: now,
          createdAt: workflowData.metadata?.createdAt || now
        }
      };

      // Save the workflow data
      localStorage.setItem(`${this.WORKFLOW_PREFIX}${id}`, JSON.stringify(savedWorkflow));
      
      // Update the index
      this.updateWorkflowIndex(id);
      
      return true;
    } catch (error) {
      console.error('Failed to save workflow:', error);
      return false;
    }
  }

  /**
   * Update the isFunction flag for a workflow
   */
  static updateWorkflowFunctionFlag(workflowId: string, isFunction: boolean): boolean {
    try {
      const workflowData = localStorage.getItem(`${this.WORKFLOW_PREFIX}${workflowId}`);
      if (!workflowData) {
        console.error('Workflow not found:', workflowId);
        return false;
      }

      const parsed = JSON.parse(workflowData);
      const updated = {
        ...parsed,
        isFunction,
        metadata: {
          ...parsed.metadata,
          updatedAt: new Date().toISOString()
        }
      };

      localStorage.setItem(`${this.WORKFLOW_PREFIX}${workflowId}`, JSON.stringify(updated));
      
      return true;
    } catch (error) {
      console.error('Failed to update workflow function flag:', error);
      return false;
    }
  }

  /**
   * Delete a workflow
   */
  static deleteWorkflow(id: string): boolean {
    try {
      localStorage.removeItem(`${this.WORKFLOW_PREFIX}${id}`);
      
      // Update the index
      const saved = localStorage.getItem(this.WORKFLOWS_KEY);
      if (saved) {
        const workflowIds: string[] = JSON.parse(saved);
        const filtered = workflowIds.filter(wid => wid !== id);
        localStorage.setItem(this.WORKFLOWS_KEY, JSON.stringify(filtered));
      }
      
      return true;
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      return false;
    }
  }

  /**
   * Check if a workflow exists
   */
  static workflowExists(id: string): boolean {
    return localStorage.getItem(`${this.WORKFLOW_PREFIX}${id}`) !== null;
  }

  /**
   * Update the workflow index
   */
  private static updateWorkflowIndex(id: string) {
    try {
      const saved = localStorage.getItem(this.WORKFLOWS_KEY);
      let workflowIds: string[] = saved ? JSON.parse(saved) : [];
      
      if (!workflowIds.includes(id)) {
        workflowIds.unshift(id); // Add to beginning
      } else {
        // Move to beginning
        workflowIds = workflowIds.filter(wid => wid !== id);
        workflowIds.unshift(id);
      }
      
      // Keep only the last 50 workflows
      if (workflowIds.length > 50) {
        workflowIds = workflowIds.slice(0, 50);
      }
      
      localStorage.setItem(this.WORKFLOWS_KEY, JSON.stringify(workflowIds));
    } catch (error) {
      console.error('Failed to update workflow index:', error);
    }
  }

  /**
   * Generate a new workflow ID
   */
  static generateWorkflowId(): string {
    return `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}